class StickyBuyButton extends HTMLElement {
  constructor() {
    super();

    this.activeClass = "wt-product__sticky-buy--show";
  }

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.priceObserver?.disconnect();
    this.purchaseObserver?.disconnect();
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onScroll);
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  initialize() {
    const productSection = this.closest(".wt-product__wrapper");
    const addToCartModule = productSection?.querySelector(
      ".wt-product__add-to-cart",
    );
    const btn = this.querySelector(".js-add-to-cart");
    const buyNowButton = this.querySelector("[data-sticky-buy-now]");
    const addToCartButton = addToCartModule?.querySelector('[name="add"]');
    const productForm = addToCartModule?.querySelector("form");
    const mainPrice = productSection?.querySelector(".wt-product__price");
    const stickyPrice = this.querySelector(".wt-product__sticky-buy__price");
    const footer = document.querySelector(".wt-footer");

    if (!addToCartModule || !addToCartButton || !productForm || !btn) return;

    // Keep the fixed price in sync when a customer selects another variant.
    if (mainPrice && stickyPrice) {
      const syncPrice = () => {
        stickyPrice.innerHTML = mainPrice.innerHTML;
      };

      this.priceObserver = new MutationObserver(syncPrice);
      this.priceObserver.observe(mainPrice, { childList: true, subtree: true });
      syncPrice();
    }

    const syncPurchaseButton = () => {
      const sourceText = addToCartButton.querySelector("span")?.textContent?.trim();
      const stickyText = btn.querySelector("span");
      const disabled =
        addToCartButton.disabled ||
        addToCartButton.getAttribute("aria-disabled") === "true";

      btn.disabled = disabled;
      if (buyNowButton) buyNowButton.disabled = disabled;
      btn.classList.toggle("loading", addToCartButton.classList.contains("loading"));
      buyNowButton?.classList.toggle(
        "loading",
        addToCartButton.classList.contains("loading"),
      );
      btn.setAttribute("aria-label", sourceText || window.variantStrings.addToCart);
      if (stickyText && sourceText) stickyText.textContent = sourceText;
    };

    this.purchaseObserver = new MutationObserver(syncPurchaseButton);
    this.purchaseObserver.observe(addToCartButton, {
      attributes: true,
      childList: true,
      subtree: true,
    });
    syncPurchaseButton();

    btn.addEventListener("click", (e) => {
      e.preventDefault();
      if (btn.disabled) return;
      addToCartButton.click();
    });

    // Reuse Shopify's existing dynamic Buy Now button, so the sticky control
    // always opens the store's configured checkout provider.
    buyNowButton?.addEventListener("click", (e) => {
      e.preventDefault();
      if (buyNowButton.disabled) return;

      const dynamicCheckoutButton = addToCartModule.querySelector(
        ".shopify-payment-button__button",
      );
      dynamicCheckoutButton?.click();
    });

    // The sticky button is useful only after the real purchase controls have
    // been scrolled above the viewport. This avoids two buy buttons on load.
    const updateVisibility = () => {
      const addToCartRect = addToCartModule.getBoundingClientRect();
      const footerRect = footer?.getBoundingClientRect();
      const purchaseControlsPassed = addToCartRect.bottom <= 0;
      const footerIsVisible =
        footerRect && footerRect.top < window.innerHeight && footerRect.bottom > 0;

      const alwaysVisibleOnMobile = window.matchMedia("(max-width: 899px)").matches;

      this.classList.toggle(
        this.activeClass,
        alwaysVisibleOnMobile || (purchaseControlsPassed && !footerIsVisible),
      );
    };

    this.onScroll = () => {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        updateVisibility();
      });
    };

    this.observer = new IntersectionObserver(updateVisibility, {
      threshold: 0,
    });

    this.observer.observe(addToCartModule);
    if (footer) this.observer.observe(footer);
    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onScroll, { passive: true });
    updateVisibility();
  }
}

customElements.define("sticky-buy-button", StickyBuyButton);

if (!customElements.get("gallery-fashion")) {
  customElements.define(
    "gallery-fashion",
    class GalleryFashion extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventsWhenDesignMode();
      }

      addEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.addEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      removeEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.removeEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.removeEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      reinitAfterDelay() {
        setTimeout(() => this.reinit(), 0);
      }

      isFirstSection() {
        const sectionWrapper = document.querySelector("#root");
        const firstSection = sectionWrapper.querySelector("section");
        this.section = this.closest("section");
        const currentSection = this.section;

        return firstSection === currentSection;
      }

      handleResize() {
        this.setTopMargin();
        this.positioningProductInfo();
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--fashion-transparent";

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        observer.observe(this.querySelector(".wt-product__gallery"));
      }

      calculateOffset() {
        const header = document.querySelector("header");
        const headerHeight = header.offsetHeight;
        const offset =
          this.isTransparentHeaderEnabled() && this.isFirstSection()
            ? headerHeight
            : 0;

        return offset;
      }

      setTopMargin() {
        const offset = this.calculateOffset();
        if (offset) {
          this.section.style.marginTop = `-${offset}px`;
        } else {
          this.section.style.marginTop = 0;
        }
      }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          header.classList.contains("wt-header--v3")
        );
      }

      renderProgressBar() {
        const thumbsGallery = this.querySelector("[data-thumbs]");
        const progressBarElement = document.createElement("div");
        progressBarElement.classList.add("gallery-fashion__progress-bar");
        const progressBarIndicatorElement = document.createElement("div");
        progressBarIndicatorElement.classList.add(
          "gallery-fashion__progress-bar-indicator",
        );
        progressBarElement.appendChild(progressBarIndicatorElement);
        thumbsGallery.appendChild(progressBarElement);

        function updateProgressBar() {
          const images = this.querySelector("gallery-section");
          const progressBar = progressBarIndicatorElement;

          const scrolled = window.scrollY;
          const maxHeight = images.clientHeight - window.innerHeight;
          const scrollPercentage = (scrolled / maxHeight) * 100;

          const progressBarHeight = progressBarElement.clientHeight;
          const progressHeight = progressBar.clientHeight;
          const maxProgressTop = progressBarHeight - progressHeight;
          const progressTop = Math.min(
            (scrollPercentage / 100) * maxProgressTop,
            maxProgressTop,
          );

          progressBar.style.top = `${progressTop}px`;

          if (progressTop === maxProgressTop) {
            thumbsGallery.classList.add("finished");
          } else {
            thumbsGallery.classList.remove("finished");
          }
        }

        window.addEventListener("scroll", updateProgressBar.bind(this));

        function handleThumbsClick(event) {
          if (event.target.className === "wt-product__img") {
            const thumbnails = Array.from(
              event.currentTarget.querySelectorAll("img"),
            );
            const index = thumbnails.indexOf(event.target);

            const fullImage = this.querySelectorAll(
              ".wt-masonry__wrapper .wt-product__img, .wt-masonry__wrapper .wt-product__thumbnail-video",
            )[index];

            fullImage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        thumbsGallery.addEventListener("click", handleThumbsClick.bind(this));
      }

      positioningProductInfo() {
        const productInfo = this.querySelector(".wt-product__main");
        const mediaDesktop = window.matchMedia("(min-width: 1200px)");

        if (mediaDesktop.matches) {
          productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
        }

        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            if (mediaDesktop.matches) {
              productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
            }
          }
        });

        const observedElement = productInfo;
        resizeObserver.observe(observedElement);
      }

      attachEvents() {
        window.addEventListener("resize", this.handleResize.bind(this));
      }

      init() {
        this.reinitAfterDelay = this.reinitAfterDelay.bind(this);
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
          this.attachEvents();
        }

        this.renderProgressBar();
        this.positioningProductInfo();
        this.addEventsWhenDesignMode();
      }

      reinit() {
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
        }
        this.positioningProductInfo();
      }
    },
  );
}
